import { firstValueFrom, retry } from 'rxjs';
import { IOC_TYPES } from '../context/ioc.types';
import { container } from '../context/module';
import { FileUpload } from '../models/file-upload';
import { FileUploadRequestMetadata } from '../models/file-upload-request-metadata';
import { UploadInProgressFile, UploadInProgressFileFactory } from '../models/upload-in-progress-file';
import { ApiConfig } from '../services/api-config';
import { LightHttp } from '../services/light-http.service';
import { ConvertOptionCategories } from '../types/dropdown.type';
import { PageData } from '../types/route-types';
import { FromType, OriginalInputFileLocationTypes, ProcessType, StatusTypes, ToType } from '../types/types';
import { FilenameUtils } from './filename-utils';
import { StringUtils } from './string-utils';

export class FileUploadUtils {
    public static async newFileId(): Promise<string> {
        const apiConfig: ApiConfig = container.get(IOC_TYPES.ApiConfig);
        const http: LightHttp = container.get(IOC_TYPES.LightHttp);
        const uploadInProgressFile = http.get<{ id: string}>(apiConfig.generateFileId()).pipe(
            retry(5),
        );
        const fileIdResp = await firstValueFrom(uploadInProgressFile);
        return fileIdResp.id;
    }

    public static async toUploadInProgressFile<T>(
        fileUpload: FileUpload,
    ): Promise<UploadInProgressFile<T>[]> {
        const inProgressFiles: UploadInProgressFile<T>[] = [];
        for (const originalInputFile of fileUpload.originalInputFiles) {
            const uploadInProgressFile: UploadInProgressFile<T> =
                await UploadInProgressFileFactory.newUploadInProgressFile(
                    null,
                    null,
                    fileUpload.uniqueProcessType,
                );
            uploadInProgressFile.file = {
                lastModified: originalInputFile.metadata.flastModified,
                name: originalInputFile.metadata.fname,
                size: originalInputFile.metadata.fsize,
                type: originalInputFile.metadata.ftype,
                slice: () => null,
            } as any;
            if (fileUpload.metadataList && fileUpload.metadataList.length > 0) {
                uploadInProgressFile.metadata = fileUpload.metadataList.find(l => {
                    if (originalInputFile.storageType === OriginalInputFileLocationTypes.LOCAL) {
                        if (l.processorMetadataList[0].fileMetadata.safeName === originalInputFile.location) {
                            return true;
                        }
                    } else if (originalInputFile.storageType === OriginalInputFileLocationTypes.GSC) {
                        if (l.processorMetadataList[0].fileMetadata.gcName === originalInputFile.gcName) {
                            return true;
                        }
                    }
                    return false;
                });
            } else {
                uploadInProgressFile.metadata = fileUpload.metadata;
            }

            uploadInProgressFile.metadata = new FileUploadRequestMetadata(
                uploadInProgressFile.metadata.processorMetadataList,
                uploadInProgressFile.metadata.file,
            );

            uploadInProgressFile.uuid = StringUtils.uuid();
            uploadInProgressFile.progress = 100;
            uploadInProgressFile.completed = true;
            uploadInProgressFile.error = false;
            uploadInProgressFile.setResponse(originalInputFile);
            uploadInProgressFile.orientation = { angle: '0', isFliped: 'false', };

            uploadInProgressFile.setComplete();
            inProgressFiles.push(uploadInProgressFile)

        }
        return inProgressFiles;
    }

    public static toFileUpload<T>(
        uploadInProgressFile: UploadInProgressFile<T>,
        wsId: string,
        processType: ProcessType,
        fromType: FromType[],
        toType: ToType,
        options: any,
        optionsSnapshot: ConvertOptionCategories,
        uniqueProcessType: string,
        pageData?: PageData
    ): FileUpload {
        const fileUpload = new FileUpload();

        fileUpload.id = StringUtils.uuid();
        fileUpload.wsId = wsId;
        fileUpload.uploadStartTime = new Date();
        fileUpload.progress = 0;
        fileUpload.result = undefined;
        fileUpload.zipResult = undefined;
        fileUpload.status = StatusTypes.UNDEFINED;
        fileUpload.name = (uploadInProgressFile.file as any).name;
        fileUpload.size = (uploadInProgressFile.file as any).size;
        fileUpload.file = uploadInProgressFile.file as any;
        fileUpload.readableMessage = undefined;
        fileUpload.ws = undefined;
        fileUpload.processType = processType;
        fileUpload.fromType = fromType;
        fileUpload.extension = [FilenameUtils.getExtension((uploadInProgressFile.file as any).name)];
        fileUpload.toType = toType;
        fileUpload.options = options;
        fileUpload.discartWSReply = false;
        fileUpload.originalInputFiles = [uploadInProgressFile.getResponse()];
        fileUpload.endpointData = uploadInProgressFile.getMetadata();
        fileUpload.settings = {} as any;
        // TODO handle uploadInProgressFile
        fileUpload.settings.email = '';
        fileUpload.processStartTime = new Date();
        fileUpload.optionsSnapshot = optionsSnapshot;
        fileUpload.metadata = uploadInProgressFile.getMetadata();
        fileUpload.uniqueProcessType = uniqueProcessType;
        fileUpload.pageData = pageData
        return fileUpload;
    }
}
